import React, { useEffect, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';

import {
  DaySelector,
  DaySelectorDays,
  DaySelectorItem,
  DaySelectorLabel,
  DaySelectorCircle,
  DayActionsPanel,
  DayIconButton,
  DayEventsContainerRelative,
  DayViewContainer,
  DayTimeSlotRow,
  DayEventInfo,
  DayEventMeta,
  DayEventTimeSlot,
  DayEventTime,
  DayEventName,
  DayEmptyText,
  DayCurrentTimeRow,
  DayCurrentTimeDot,
  DayCurrentTimeLabel,
  DayEventRecurrenceIcon,
  DayEventVideoButton,
  DayEventPriceContainer,
  DayEventStatusWrapper,
  DayEventNotificationIcon
} from '../../../Css/StyledCalendarDay.js';

import { CommonIcon } from '../../../Css/StyledCalendarMobile.js';

import {
  isSameDay,
  formatAppointmentPrice,
  isOnlineAppointment,
  isEventInPast
} from '../../../../utils/calendarUtils.js';

import {
  getNotificationProblemBadgeContent,
  getNotificationProblemTooltip,
  shouldShowNotificationProblemIndicator
} from '../../../../utils/notificationCalendarUtils.js';

import {
  DAY_TIME_SLOTS,
  getAppointmentCalendarColor
} from '../../../../constants/calendarConstants.js';

import videoImage from '../../../../image/video.png';
import recurrenteImage from '../../../../image/recurrente.png';
import StatusDropdown from '../StatusDropdown.js';
import { PlusIcon } from '../CalendarIcons.js';

const DEFAULT_VISIBLE_TIME_RANGES = [
  {
    start: '06:00',
    end: '23:00'
  }
];

// Convierte una hora "HH:mm" a minutos totales.
// Sirve para ordenar horarios y comparar si están dentro del rango visible.
const timeToMinutes = (time) => {
  if (!time) {
    return 0;
  }

  const [hours, minutes = 0] = time.split(':').map(Number);

  return (hours * 60) + minutes;
};

// Normaliza los rangos visibles que vienen desde la ruedita.
// El horario "hasta" es inclusivo.
// Ejemplo: 08:00 a 10:00 muestra 08:00, 09:00 y 10:00.
const normalizeVisibleRanges = (calendarViewSettings) => {
  const ranges = calendarViewSettings?.visibleTimeRanges;

  if (!Array.isArray(ranges) || ranges.length === 0) {
    return DEFAULT_VISIBLE_TIME_RANGES.map((range) => ({
      ...range,
      startMinutes: timeToMinutes(range.start),
      endMinutes: timeToMinutes(range.end) + 60
    }));
  }

  return ranges
    .filter((range) => range?.start && range?.end && range.start <= range.end)
    .map((range) => ({
      start: range.start,
      end: range.end,
      startMinutes: timeToMinutes(range.start),
      endMinutes: timeToMinutes(range.end) + 60
    }))
    .sort((rangeA, rangeB) => rangeA.startMinutes - rangeB.startMinutes);
};

// Revisa si una hora queda dentro de alguno de los rangos visibles.
const isTimeInsideVisibleRanges = (time, visibleRanges) => {
  const timeMinutes = timeToMinutes(time);

  return visibleRanges.some((range) => (
    timeMinutes >= range.startMinutes &&
    timeMinutes < range.endMinutes
  ));
};

// Devuelve los horarios base que deben verse en la vista Día.
// Respeta la configuración de la ruedita.
const getVisibleTimeSlots = (visibleRanges) => {
  return DAY_TIME_SLOTS.filter((timeSlot) =>
    isTimeInsideVisibleRanges(timeSlot, visibleRanges)
  );
};

// Determina si un turno debe mostrarse según su hora de inicio.
const isAppointmentInsideVisibleRanges = (appointment, visibleRanges) => {
  if (!appointment?.hora_inicio) {
    return false;
  }

  return isTimeInsideVisibleRanges(appointment.hora_inicio, visibleRanges);
};

// Formatea una fecha YYYY-MM-DD a DD/MM/YYYY para mostrar en el warning.
const formatDisplayDate = (dateString) => {
  if (!dateString) {
    return '';
  }

  const [year, month, day] = dateString.split('-');

  if (!year || !month || !day) {
    return dateString;
  }

  return `${day}/${month}/${year}`;
};

// Arma el mensaje del SweetAlert cuando hay turnos ocultos por el rango visible.
const getHiddenAppointmentsMessage = (hiddenAppointments) => {
  if (!hiddenAppointments.length) {
    return '';
  }

  const cantidad = hiddenAppointments.length;

  const ejemplos = hiddenAppointments
    .slice(0, 5)
    .map((appointment) => {
      const paciente =
        appointment.paciente_nombre_completo ||
        `${appointment.paciente_nombre || ''} ${appointment.paciente_apellido || ''}`.trim() ||
        'Paciente sin nombre';

      return `• ${formatDisplayDate(appointment.fecha_turno)} ${appointment.hora_inicio} - ${paciente}`;
    })
    .join('\n');

  const textoCantidad =
    cantidad === 1
      ? 'Hay 1 turno fuera del horario visible.'
      : `Hay ${cantidad} turnos fuera del horario visible.`;

  const textoFinal =
    cantidad > 5
      ? '\n\nSe muestran solo los primeros 5.'
      : '';

  return `${textoCantidad}\n\nNo se borraron ni desaparecieron. Solo están ocultos por la configuración de vista.\n\nTurnos ocultos:\n${ejemplos}${textoFinal}\n\nPara verlos, tocá la ruedita ⚙ y ampliá el horario visible o elegí “Mostrar todo el día”.`;
};

// Escapa texto para mostrarlo como HTML dentro de SweetAlert.
const escapeHtml = (value) => {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

// Determina si el día seleccionado es hoy.
// Solo en ese caso mostramos y usamos la fila de hora actual.
const isSelectedDateToday = (selectedDate) => {
  const now = new Date();

  return (
    selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate()
  );
};

// Devuelve la hora actual en formato "HH:mm".
const getCurrentTimeString = () => {
  const now = new Date();

  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

// Revisa si un turno pertenece a una serie.
const isRecurringAppointment = (appointment) => {
  if (!appointment) {
    return false;
  }

  return Boolean(
    appointment.repeat_group_id ||
    appointment.repetir_turno ||
    (
      appointment.repeat &&
      appointment.repeat !== 'no'
    ) ||
    (
      appointment.repeat_type &&
      appointment.repeat_type !== 'no'
    )
  );
};

// Revisa si el turno es virtual.
// Usa modalidad_turno nueva y mantiene compatibilidad con turnos viejos.
const isVirtualAppointment = (appointment) => {
  if (!appointment) {
    return false;
  }

  return (
    appointment.modalidad_turno === 'virtual' ||
    isOnlineAppointment(appointment.lugar)
  );
};

// Devuelve los honorarios contemplando campos nuevos y viejos.
// El campo principal esperado ahora es honorarios.
const getAppointmentPrice = (appointment) => {
  if (!appointment) {
    return '';
  }

  return appointment.honorarios ?? appointment.valor_sesion ?? '';
};

// Construye las filas de la vista Día.
// Combina:
// - horarios base visibles;
// - horarios reales de turnos;
// - fila de hora actual, si corresponde.
// Así se muestran todos los horarios y no solo los turnos existentes.
const buildDayRows = ({
  visibleTimeSlots,
  dayAppointments,
  visibleRanges,
  selectedDate
}) => {
  const rowsByTime = new Map();

  visibleTimeSlots.forEach((time) => {
    rowsByTime.set(time, {
      type: 'empty',
      time,
      appointments: []
    });
  });

  dayAppointments.forEach((appointment) => {
    if (!appointment?.hora_inicio) {
      return;
    }

    if (!isAppointmentInsideVisibleRanges(appointment, visibleRanges)) {
      return;
    }

    const existingRow = rowsByTime.get(appointment.hora_inicio);

    if (existingRow) {
      existingRow.appointments.push(appointment);
      existingRow.type = 'appointment';
      return;
    }

    rowsByTime.set(appointment.hora_inicio, {
      type: 'appointment',
      time: appointment.hora_inicio,
      appointments: [appointment]
    });
  });

  const rows = Array.from(rowsByTime.values())
    .sort((rowA, rowB) => timeToMinutes(rowA.time) - timeToMinutes(rowB.time));

  const currentTime = getCurrentTimeString();

  if (
    isSelectedDateToday(selectedDate) &&
    isTimeInsideVisibleRanges(currentTime, visibleRanges)
  ) {
    rows.push({
      type: 'current-time',
      time: currentTime,
      appointments: []
    });
  }

  return rows
    .sort((rowA, rowB) => {
      const diff = timeToMinutes(rowA.time) - timeToMinutes(rowB.time);

      if (diff !== 0) {
        return diff;
      }

      if (rowA.type === 'current-time') {
        return -1;
      }

      if (rowB.type === 'current-time') {
        return 1;
      }

      return 0;
    });
};

const DayView = ({
  daySelectorDays,
  selectedDate,
  dayAppointments = [],
  onSelectDate,
  onStatusChange,
  onAppointmentClick,
  onTimeSlotClick,
  onAddAppointment,
  onOpenSettings,
  calendarViewSettings
}) => {
  const currentTimeRowRef = useRef(null);

  // Normaliza la configuración horaria que viene de backend/Redux.
  const visibleRanges = useMemo(
    () => normalizeVisibleRanges(calendarViewSettings),
    [calendarViewSettings]
  );

  // Calcula los horarios base que se van a mostrar.
  const visibleTimeSlots = useMemo(
    () => getVisibleTimeSlots(visibleRanges),
    [visibleRanges]
  );

  // Calcula los turnos del día que quedan ocultos por el filtro horario.
  // Si hay alguno, mostramos el warning ⚠.
  const hiddenAppointments = useMemo(() => {
    return dayAppointments.filter((appointment) => {
      return !isAppointmentInsideVisibleRanges(appointment, visibleRanges);
    });
  }, [dayAppointments, visibleRanges]);

  const hasHiddenAppointments = hiddenAppointments.length > 0;

  // Construye las filas visibles del día: horarios vacíos, turnos y hora actual.
  const dayRows = useMemo(() => {
    return buildDayRows({
      visibleTimeSlots,
      dayAppointments,
      visibleRanges,
      selectedDate
    });
  }, [visibleTimeSlots, dayAppointments, visibleRanges, selectedDate]);

  // Si está activada la opción de posicionarse en la hora actual,
  // la vista Día scrollea hasta la fila de "Ahora".
  useEffect(() => {
    if (calendarViewSettings?.autoScrollToCurrentTime === false) {
      return;
    }

    if (!currentTimeRowRef.current) {
      return;
    }

    currentTimeRowRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [
    dayRows,
    calendarViewSettings?.autoScrollToCurrentTime
  ]);

  // Cambia el estado de un turno desde el dropdown.
  const handleStatusChange = (appointmentId, newStatus) => {
    if (onStatusChange) {
      onStatusChange(appointmentId, newStatus);
    }
  };

  // Si la fila tiene turno, abre edición.
  // Si la fila está vacía, abre creación en ese día y horario.
  const handleRowClick = (row) => {
    if (row.appointments?.length > 0) {
      if (onAppointmentClick) {
        onAppointmentClick(row.appointments[0]);
      }

      return;
    }

    if (row.type === 'current-time') {
      return;
    }

    if (onTimeSlotClick && row.time) {
      onTimeSlotClick(selectedDate, row.time);
    }
  };

  // Botón + de la vista Día.
  const handleAddAppointment = () => {
    if (onAddAppointment) {
      onAddAppointment();
    }
  };

  // Botón de configuración ⚙.
  const handleOpenSettingsClick = () => {
    if (onOpenSettings) {
      onOpenSettings();
    }
  };

  // Warning ⚠ con SweetAlert.
  const handleHiddenAppointmentsWarningClick = async () => {
    const message = getHiddenAppointmentsMessage(hiddenAppointments);

    if (!message) {
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hay turnos ocultos',
      html: `
        <div style="text-align: left; white-space: pre-line; font-size: 14px;">
          ${escapeHtml(message)}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Abrir configuración',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: '#E7973C',
      cancelButtonColor: '#6B7280',
      allowOutsideClick: true,
      allowEscapeKey: true
    });

    if (result.isConfirmed && onOpenSettings) {
      onOpenSettings();
    }
  };

  return (
    <DayViewContainer>
      <DaySelector>
        <DaySelectorDays>
          {daySelectorDays.map((day) => {
            const isActive = isSameDay(day.date, selectedDate);

            return (
              <DaySelectorItem
                key={`selector-${day.date.toISOString()}`}
                type="button"
                onClick={() => onSelectDate(day.date)}
              >
                <DaySelectorLabel>{day.label}</DaySelectorLabel>
                <DaySelectorCircle active={isActive}>{day.dateNumber}</DaySelectorCircle>
              </DaySelectorItem>
            );
          })}
        </DaySelectorDays>

        <DayActionsPanel>
          <DayIconButton
            type="button"
            onClick={handleAddAppointment}
            title="Agregar turno"
            aria-label="Agregar turno"
          >
            <PlusIcon size={16} />
          </DayIconButton>

          <DayIconButton
            type="button"
            onClick={handleOpenSettingsClick}
            title="Configurar vista"
            aria-label="Configurar vista"
          >
            ⚙
          </DayIconButton>

          {hasHiddenAppointments && (
            <DayIconButton
              type="button"
              warning
              onClick={handleHiddenAppointmentsWarningClick}
              title="Hay turnos ocultos fuera del horario visible"
              aria-label="Hay turnos ocultos"
            >
              ⚠
            </DayIconButton>
          )}
        </DayActionsPanel>
      </DaySelector>

      <DayEventsContainerRelative>
        {dayRows.map((row, rowIndex) => {
          if (row.type === 'current-time') {
            return (
              <DayCurrentTimeRow
                key={`current-time-${row.time}`}
                ref={currentTimeRowRef}
              >
                <DayCurrentTimeDot />
                <DayCurrentTimeLabel>
                  Ahora {row.time}
                </DayCurrentTimeLabel>
              </DayCurrentTimeRow>
            );
          }

          const appointmentsToRender =
            row.appointments.length > 0
              ? row.appointments
              : [null];

          return appointmentsToRender.map((appointment, appointmentIndex) => {
            const isEmpty = !appointment;

            const appointmentId =
              appointment?.id_turno ||
              appointment?.id ||
              `empty-${row.time}-${rowIndex}-${appointmentIndex}`;

            return (
              <DayEventTimeSlot key={`day-slot-${appointmentId}`}>
                <DayTimeSlotRow
                  isEmpty={isEmpty}
                  isPast={appointment ? isEventInPast(appointment) : false}
                  $accentColor={appointment ? getAppointmentCalendarColor(appointment) : null}
                  onClick={() => handleRowClick(row)}
                  style={{ cursor: 'pointer' }}
                >
                  <DayEventInfo>
                    <DayEventTime>
                      {appointment
                        ? `${appointment.hora_inicio} - ${appointment.hora_fin}`
                        : row.time}
                    </DayEventTime>

                    {appointment ? (
                      <DayEventName>
                        {appointment.paciente_nombre_completo || 'Paciente sin nombre'}
                      </DayEventName>
                    ) : (
                      <DayEmptyText>
                        Disponible
                      </DayEmptyText>
                    )}
                  </DayEventInfo>

                  {appointment ? (
                    <DayEventMeta>
                      {isRecurringAppointment(appointment) && (
                        <DayEventRecurrenceIcon title="Sesión recurrente">
                          <CommonIcon
                            src={recurrenteImage}
                            alt="Recurrente"
                          />
                        </DayEventRecurrenceIcon>
                      )}

                      {shouldShowNotificationProblemIndicator(appointment) && (
                        <DayEventNotificationIcon
                          $variant="danger"
                          title={getNotificationProblemTooltip(appointment)}
                          aria-label={getNotificationProblemTooltip(appointment)}
                        >
                          <span className="notification-mail-symbol">
                            ✉
                          </span>

                          <span className="notification-status-badge">
                            {getNotificationProblemBadgeContent(appointment)}
                          </span>
                        </DayEventNotificationIcon>
                      )}

                      {isVirtualAppointment(appointment) && appointment.link_reunion && (
                        <DayEventVideoButton
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(appointment.link_reunion, '_blank');
                          }}
                          title="Hacer clic para unirse a la videollamada"
                        >
                          <CommonIcon
                            src={videoImage}
                            alt="Video"
                          />
                        </DayEventVideoButton>
                      )}

                      <DayEventPriceContainer>
                        {formatAppointmentPrice(getAppointmentPrice(appointment))}
                      </DayEventPriceContainer>

                      <DayEventStatusWrapper onClick={(e) => e.stopPropagation()}>
                        <StatusDropdown
                          currentStatus={appointment.estado}
                          onStatusChange={handleStatusChange}
                          appointmentId={appointment.id_turno || appointment.id}
                        />
                      </DayEventStatusWrapper>
                    </DayEventMeta>
                  ) : (
                    <DayEventMeta>
                      <DayEmptyText>
                        Tocá para agregar
                      </DayEmptyText>
                    </DayEventMeta>
                  )}
                </DayTimeSlotRow>
              </DayEventTimeSlot>
            );
          });
        })}
      </DayEventsContainerRelative>
    </DayViewContainer>
  );
};

export default DayView;